import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { tr } from "../i18n/translations";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Shield, Truck, RotateCcw, Award } from "lucide-react";

export default function Footer() {
  const { lang } = useApp();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  async function subscribe(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, lang });
      toast.success(tr(lang, "popup_ok"));
      setEmail("");
    } catch {
      toast.error("Erreur, réessayez.");
    } finally { setLoading(false); }
  }

  return (
    <footer className="bg-ink text-ivory mt-24" data-testid="site-footer">
      <div className="border-b border-ivory/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <TrustItem Icon={Shield} title="Paiement sécurisé" sub="PayPal · SSL 256-bit" />
            <TrustItem Icon={Truck} title="Livraison offerte" sub="dès 100€ (Europe) · 150€ (Afrique) · 200€ (monde)" />
            <TrustItem Icon={RotateCcw} title="Satisfait ou remboursé" sub="14 jours rétractation" />
            <TrustItem Icon={Award} title="Sélection cliniciens" sub="par des pros pour des pros" />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-2">
            <p className="font-display text-2xl mb-3">orthophonie<span className="italic font-light">.online</span></p>
            <p className="text-xs text-ivory/65 leading-relaxed mb-5">
              {"Boutique premium dédiée à l'orthophonie, à l'apprentissage spécialisé et à la rééducation. Matériel sélectionné et vérifié par des cliniciens experts."}
            </p>
            <form onSubmit={subscribe} className="flex gap-2 max-w-sm">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tr(lang, "newsletter_placeholder") || "votre@email.com"}
                className="bg-ivory/10 border border-ivory/20 rounded-full px-4 py-2 text-sm flex-1 outline-none focus:border-coral"
                data-testid="footer-newsletter-email"
                required
              />
              <button disabled={loading} className="bg-coral hover:bg-coral-dark text-ivory px-4 py-2 rounded-full text-sm font-semibold transition" data-testid="footer-newsletter-submit">
                {loading ? "..." : "OK"}
              </button>
            </form>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-ivory/60 mb-4">Boutique</p>
            <nav className="space-y-2 text-sm">
              <Link to="/" className="block hover:text-coral transition">Accueil</Link>
              <Link to="/catalogue" className="block hover:text-coral transition">Catalogue</Link>
              <Link to="/articles" className="block hover:text-coral transition">Articles et Expertise</Link>
              <Link to="/espace-pro" className="block hover:text-coral transition">Espace Pro (B2B)</Link>
              <Link to="/fidelite" className="block hover:text-coral transition" data-testid="footer-loyalty-link">Programme fidélité</Link>
              <Link to="/mes-commandes" className="block hover:text-coral transition" data-testid="footer-orders-link">Suivre ma commande</Link>
              <Link to="/about" className="block hover:text-coral transition">Notre histoire</Link>
            </nav>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-ivory/60 mb-4">Aide & contact</p>
            <nav className="space-y-2 text-sm">
              <a href={`mailto:${settings.company_email || "contact@langageonline.fr"}`} className="block hover:text-coral transition break-all">{settings.company_email || "contact@langageonline.fr"}</a>
              <Link to="/cgv" className="block hover:text-coral transition">Livraison & retours</Link>
              <Link to="/cgv#paiement" className="block hover:text-coral transition">Modes de paiement</Link>
            </nav>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-ivory/60 mb-4">Légal</p>
            <nav className="space-y-2 text-sm">
              <Link to="/mentions-legales" className="block hover:text-coral transition">Mentions légales</Link>
              <Link to="/cgv" className="block hover:text-coral transition">CGV</Link>
              <Link to="/rgpd" className="block hover:text-coral transition">Confidentialité (RGPD)</Link>
              <a
                href="https://langage.online"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-coral transition group"
                data-testid="footer-annuaire-link"
              >
                <img
                  src="/ait-annuaire-logo.png"
                  alt="A.I.T — Annuaire International Télélangage"
                  className="w-8 h-8 rounded-full object-cover ring-1 ring-ivory/20 group-hover:ring-coral transition"
                  loading="lazy"
                />
                <span>Annuaire International Télélangage ↗</span>
              </a>
              <Link to="/affiliation" className="block hover:text-coral transition" data-testid="footer-affiliation-link">Programme d'affiliation</Link>
              <Link to="/rgpd#cookies" className="block hover:text-coral transition">Cookies</Link>
              <button
                type="button"
                onClick={() => window.openCookieBanner && window.openCookieBanner()}
                className="block text-start hover:text-coral transition"
                data-testid="footer-manage-cookies"
              >
                Gérer mes cookies
              </button>
            </nav>
          </div>
        </div>

        {/* Dossiers troubles — internal SEO linking */}
        <div className="mt-10 pt-8 border-t border-ivory/10" data-testid="footer-troubles-block">
          <p className="text-xs uppercase tracking-widest text-ivory/60 mb-4">Dossiers orthophonie</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-ivory/70">
            <Link to="/troubles/tdah" className="hover:text-coral transition" data-testid="footer-troubles-tdah">TDAH</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/dyslexie" className="hover:text-coral transition" data-testid="footer-troubles-dyslexie">Dyslexie</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/dyspraxie" className="hover:text-coral transition" data-testid="footer-troubles-dyspraxie">Dyspraxie</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/dyscalculie" className="hover:text-coral transition" data-testid="footer-troubles-dyscalculie">Dyscalculie</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/dysphasie" className="hover:text-coral transition" data-testid="footer-troubles-dysphasie">Dysphasie</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/dysorthographie" className="hover:text-coral transition" data-testid="footer-troubles-dysorthographie">Dysorthographie</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/aphasie" className="hover:text-coral transition" data-testid="footer-troubles-aphasie">Aphasie</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/autisme" className="hover:text-coral transition" data-testid="footer-troubles-autisme">Autisme (TSA)</Link>
            <span className="text-ivory/20">·</span>
            <Link to="/troubles/begaiement" className="hover:text-coral transition" data-testid="footer-troubles-begaiement">Bégaiement</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-ivory/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap justify-between items-center gap-4">
          <p className="text-xs text-ivory/55">
            © {new Date().getFullYear()} {settings.company_name || "Orthophonie Online"}
            {settings.company_siret && settings.company_siret !== "À compléter dans l'admin" && (
              <> · SIRET {settings.company_siret}</>
            )}
            {" · Hébergé par "}{settings.hosting_provider || "Emergent"}
          </p>
          <div className="flex items-center gap-2 opacity-90" data-testid="payment-logos">
            <PaymentLogo label="Visa" bg="#1A1F71" />
            <PaymentLogo label="MC" bg="#EB001B" />
            <PaymentLogo label="PayPal" bg="#003087" />
            <PaymentLogo label=" Pay" bg="#000" />
            <PaymentLogo label="G Pay" bg="#fff" textColor="#000" />
          </div>
        </div>
      </div>
    </footer>
  );
}

function TrustItem({ Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={20} className="text-coral mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-[10px] text-ivory/60">{sub}</p>
      </div>
    </div>
  );
}

function PaymentLogo({ label, bg, textColor = "#fff" }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded text-[10px] font-bold px-2 py-1 min-w-[42px]"
      style={{ background: bg, color: textColor, border: textColor === "#000" ? "1px solid #e5e5e5" : "none" }}
    >
      {label}
    </span>
  );
}
